/**
 * Navigation State Management
 * Redux slice for managing navigation and layout state
 */

import { createSlice, PayloadAction, createListenerMiddleware } from '@reduxjs/toolkit';
import React from 'react';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

export interface RouteInfo {
  title: string;
  description?: string;
  breadcrumbs: BreadcrumbItem[];
  showRefresh?: boolean;
  showSettings?: boolean;
}

export interface NavigationState {
  showMarketDataPanel: boolean;
  showNotificationsPanel: boolean;
  showUserProfilePanel: boolean;
  showMobileMenu: boolean;
  mainSidebarCollapsed: boolean;
  navbarHeaderVisible: boolean;
  mobileMenuOpen: boolean;
  isMobile: boolean;
  sidebarHiddenOnMobile: boolean;
  activePanels: string[];
  currentRoute: string;
  routeInfo: RouteInfo;
}

// =============================================================================
// ROUTE INFO GENERATION
// =============================================================================

/**
 * Generate route info based on the current path
 */
const generateRouteInfo = (path: string): RouteInfo => {
  const routeMap: Record<string, RouteInfo> = {
    '/': {
      title: 'Dashboard',
      description: 'Welcome to Mechagora Trading Platform',
      breadcrumbs: [
        { label: 'Home', href: '/' }
      ]
    },
    '/trading': {
      title: 'Trading',
      description: 'Professional trading interface with real-time market data',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Trading' }
      ]
    },
    '/trading/orders': {
      title: 'Orders',
      description: 'Manage your trading orders',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Trading', href: '/trading' },
        { label: 'Orders' }
      ]
    },
    '/trading/execution': {
      title: 'Execution',
      description: 'Order execution and monitoring',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Trading', href: '/trading' },
        { label: 'Execution' }
      ]
    },
    '/portfolio': {
      title: 'Portfolio',
      description: 'Portfolio management and analysis',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Portfolio' }
      ]
    },
    '/market-data': {
      title: 'Market Data',
      description: 'Real-time market data and analytics',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Market Data' }
      ]
    },
    '/risk-management': {
      title: 'Risk Management',
      description: 'Risk analysis and management tools',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Risk Management' }
      ]
    },
    '/admin': {
      title: 'Administration',
      description: 'System administration and settings',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Administration' }
      ]
    },
    '/profile': {
      title: 'Profile',
      description: 'User profile and account settings',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Profile' }
      ]
    },
    '/notifications': {
      title: 'Notifications',
      description: 'System notifications and alerts',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Notifications' }
      ]
    },
    '/ai-trading': {
      title: 'Agents',
      description: 'Supervise trading agents on this desk',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Agents' }
      ]
    },
    '/algo-trading': {
      title: 'Agents',
      description: 'Supervise trading agents on this desk',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Agents' }
      ]
    },
    '/chat-ai': {
      title: 'Chat',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Chat' }
      ]
    },
    '/activity': {
      title: 'Activity',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Activity' }
      ]
    },
    '/settings': {
      title: 'Settings',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Settings' }
      ]
    },
    '/client/dashboard': {
      title: 'Dashboard',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Dashboard' }
      ]
    },
    '/libraries': {
      title: 'Libraries',
      breadcrumbs: [
        { label: 'Home', href: '/' },
        { label: 'Libraries' }
      ]
    }
  };

  return routeMap[path] || {
    title: 'Page',
    description: 'Mechagora Trading Platform',
    breadcrumbs: [
      { label: 'Home', href: '/' },
      { label: 'Page' }
    ]
  };
};

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: NavigationState = {
  showMarketDataPanel: false,
  showNotificationsPanel: false,
  showUserProfilePanel: false,
  showMobileMenu: false,
  mainSidebarCollapsed: false,
  navbarHeaderVisible: false,
  mobileMenuOpen: false,
  isMobile: false,
  sidebarHiddenOnMobile: true, // Hide sidebar by default on mobile
  activePanels: [],
  currentRoute: '/',
  routeInfo: {
    title: 'Dashboard',
    description: 'Welcome to Mechagora Trading Platform',
    breadcrumbs: [
      { label: 'Home', href: '/', icon: 'HomeOutlined' }
    ]
  }
};

// =============================================================================
// SLICE DEFINITION
// =============================================================================

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    // Panel management
    togglePanel: (state, action: PayloadAction<{ panelName: string; forceState?: boolean }>) => {
      const { panelName, forceState } = action.payload;
      const panelMap: Record<string, keyof NavigationState> = {
        marketData: 'showMarketDataPanel',
        notifications: 'showNotificationsPanel',
        userProfile: 'showUserProfilePanel',
        mobileMenu: 'showMobileMenu',
      };

      const panelKey = panelMap[panelName];
      if (!panelKey) return;

      const currentState = state[panelKey] as boolean;
      const newState = forceState !== undefined ? forceState : !currentState;

      // Update panel state
      (state as any)[panelKey] = newState;

      // Update active panels array
      if (newState) {
        if (!state.activePanels.includes(panelName)) {
          state.activePanels.push(panelName);
        }
      } else {
        const index = state.activePanels.indexOf(panelName);
        if (index !== -1) {
          state.activePanels.splice(index, 1);
        }
      }
    },

    closeAllPanels: (state) => {
      state.showMarketDataPanel = false;
      state.showNotificationsPanel = false;
      state.showUserProfilePanel = false;
      state.showMobileMenu = false;
      state.activePanels = [];
    },

    // Sidebar management
    toggleMainSidebar: (state) => {
      state.mainSidebarCollapsed = !state.mainSidebarCollapsed;
      state.navbarHeaderVisible = !state.mainSidebarCollapsed;
    },

    setMainSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.mainSidebarCollapsed = action.payload;
      state.navbarHeaderVisible = !action.payload;
    },

    // Mobile navigation
    toggleMobileSidebar: (state) => {
      if (state.isMobile) {
        // On mobile: drawer open/close. Drawer always shows expanded labels
        // (full width) — never the 80px icon rail, which is unusable as a sheet.
        const nextHidden = !state.sidebarHiddenOnMobile
        state.sidebarHiddenOnMobile = nextHidden
        if (!nextHidden) {
          state.mainSidebarCollapsed = false
        }
      } else {
        // On desktop / tablet: collapse toggle for docked rail
        state.mainSidebarCollapsed = !state.mainSidebarCollapsed;
      }
    },

    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload;
    },

    setSidebarHiddenOnMobile: (state, action: PayloadAction<boolean>) => {
      state.sidebarHiddenOnMobile = action.payload;
    },

    // Device state
    setIsMobile: (state, action: PayloadAction<boolean>) => {
      state.isMobile = action.payload;
      // Reset mobile-specific states when switching to desktop
      if (!action.payload) {
        state.sidebarHiddenOnMobile = false;
        state.mobileMenuOpen = false;
      }
    },

    setNavbarHeaderVisible: (state, action: PayloadAction<boolean>) => {
      state.navbarHeaderVisible = action.payload;
    },

    // Hydration for SSR
    hydrateNavigation: (state, action: PayloadAction<Partial<NavigationState>>) => {
      // Load persisted state from localStorage
      const persistedState = action.payload;
      if (persistedState.mainSidebarCollapsed !== undefined) {
        state.mainSidebarCollapsed = persistedState.mainSidebarCollapsed;
      }
      if (persistedState.navbarHeaderVisible !== undefined) {
        state.navbarHeaderVisible = persistedState.navbarHeaderVisible;
      }
    },

    // Route navigation
    setCurrentRoute: (state, action: PayloadAction<string>) => {
      state.currentRoute = action.payload;
      state.routeInfo = generateRouteInfo(action.payload);
    },

    setRouteInfo: (state, action: PayloadAction<RouteInfo>) => {
      state.routeInfo = action.payload;
    },

    updateRouteInfo: (state, action: PayloadAction<Partial<RouteInfo>>) => {
      state.routeInfo = { ...state.routeInfo, ...action.payload };
    },

    // Reset state
    resetNavigation: () => initialState,
  },
});

// =============================================================================
// ACTIONS & SELECTORS
// =============================================================================

export const {
  togglePanel,
  closeAllPanels,
  toggleMainSidebar,
  setMainSidebarCollapsed,
  toggleMobileSidebar,
  setMobileMenuOpen,
  setSidebarHiddenOnMobile,
  setIsMobile,
  setNavbarHeaderVisible,
  hydrateNavigation,
  setCurrentRoute,
  setRouteInfo,
  updateRouteInfo,
  resetNavigation,
} = navigationSlice.actions;

// Selectors
export const selectNavigationState = (state: { navigation: NavigationState }) => state.navigation;
export const selectShowMarketDataPanel = (state: { navigation: NavigationState }) => state.navigation.showMarketDataPanel;
export const selectShowNotificationsPanel = (state: { navigation: NavigationState }) => state.navigation.showNotificationsPanel;
export const selectShowUserProfilePanel = (state: { navigation: NavigationState }) => state.navigation.showUserProfilePanel;
export const selectShowMobileMenu = (state: { navigation: NavigationState }) => state.navigation.showMobileMenu;
export const selectMainSidebarCollapsed = (state: { navigation: NavigationState }) => state.navigation.mainSidebarCollapsed;
export const selectNavbarHeaderVisible = (state: { navigation: NavigationState }) => state.navigation.navbarHeaderVisible;
export const selectMobileMenuOpen = (state: { navigation: NavigationState }) => state.navigation.mobileMenuOpen;
export const selectIsMobile = (state: { navigation: NavigationState }) => state.navigation.isMobile;
export const selectSidebarHiddenOnMobile = (state: { navigation: NavigationState }) => state.navigation.sidebarHiddenOnMobile;
export const selectActivePanels = (state: { navigation: NavigationState }) => state.navigation.activePanels;

// Complex selectors
export const selectIsSidebarVisible = (state: { navigation: NavigationState }) => {
  const nav = state.navigation;
  if (nav.isMobile) {
    return !nav.sidebarHiddenOnMobile;
  }
  return !nav.mainSidebarCollapsed;
};

export const selectActivePanelCount = (state: { navigation: NavigationState }) =>
  state.navigation.activePanels.length;

export const selectIsPanelOpen = (panelName: string) => (state: { navigation: NavigationState }) => {
  const panelMap: Record<string, keyof NavigationState> = {
    marketData: 'showMarketDataPanel',
    notifications: 'showNotificationsPanel',
    userProfile: 'showUserProfilePanel',
    mobileMenu: 'showMobileMenu',
  };
  const panelKey = panelMap[panelName];
  return panelKey ? state.navigation[panelKey] as boolean : false;
};

// Route selectors
export const selectCurrentRoute = (state: { navigation: NavigationState }) => state.navigation.currentRoute;
export const selectRouteInfo = (state: { navigation: NavigationState }) => state.navigation.routeInfo;
export const selectRouteBreadcrumbs = (state: { navigation: NavigationState }) => state.navigation.routeInfo.breadcrumbs;
export const selectRouteTitle = (state: { navigation: NavigationState }) => state.navigation.routeInfo.title;
export const selectRouteDescription = (state: { navigation: NavigationState }) => state.navigation.routeInfo.description;

// =============================================================================
// PERSISTENCE MIDDLEWARE
// =============================================================================

/**
 * Listener middleware for persisting navigation state to localStorage
 * (key `navigation-store`, read back by useNavigationHydration). Registered in
 * makeStore (shared/providers/store).
 *
 * Only the user's own expand/collapse toggle is saved. Forced collapses (live /trading
 * <= 1440px in TradingPage, the shell's tablet band, the /trading/preview default),
 * setIsMobile, the mobile drawer and hydrateNavigation itself are not, so they never
 * overwrite the saved setting and hydration cannot loop back into a write.
 */
export const navigationListener = createListenerMiddleware();

navigationListener.startListening({
  predicate: (action, currentState) =>
    toggleMainSidebar.match(action) ||
    (toggleMobileSidebar.match(action) &&
      !(currentState as { navigation: NavigationState }).navigation.isMobile),
  effect: async (action, api) => {
    const state = api.getState() as { navigation: NavigationState };

    if (typeof window !== 'undefined') {
      // Only persist specific fields
      const persistedState = {
        mainSidebarCollapsed: state.navigation.mainSidebarCollapsed,
        navbarHeaderVisible: state.navigation.navbarHeaderVisible,
      };

      try {
        localStorage.setItem('navigation-store', JSON.stringify(persistedState));
      } catch (error) {
        console.warn('Failed to persist navigation state:', error);
      }
    }
  },
});

// =============================================================================
// INITIALIZATION HELPER
// =============================================================================

/**
 * Load navigation state from localStorage
 */
export const loadNavigationState = (): Partial<NavigationState> => {
  if (typeof window === 'undefined') return {};

  try {
    const stored = localStorage.getItem('navigation-store');
    const parsed: unknown = stored ? JSON.parse(stored) : null;
    if (!parsed || typeof parsed !== 'object') return {};
    // Same shape the listener writes; ignore anything else.
    const saved = parsed as Record<string, unknown>;
    const out: Partial<NavigationState> = {};
    if (typeof saved.mainSidebarCollapsed === 'boolean') out.mainSidebarCollapsed = saved.mainSidebarCollapsed;
    if (typeof saved.navbarHeaderVisible === 'boolean') out.navbarHeaderVisible = saved.navbarHeaderVisible;
    return out;
  } catch (error) {
    console.warn('Failed to load navigation state:', error);
    return {};
  }
};

export default navigationSlice.reducer;

